// Update DrillDownModal.jsx with enhanced content
import React from 'react';
import { DrilldownDataService } from '../services/DrilldownDataService';
import './DrillDownModal.css';

const DrillDownModal = ({ isOpen, onClose, title, data, type }) => {
  if (!isOpen) return null;

  // Get detailed data based on type
  const getDetailedData = () => {
    if (!data) return null;
    
    switch(type) {
      case 'commodity':
      case 'commodity-details':
        return DrilldownDataService.getCommodityDetails(data.id || data.name?.toLowerCase().replace(' ', '-'));
      case 'entity':
      case 'entity-traffic':
        return DrilldownDataService.getEntityDetails(data.id || data.name?.split(' ')[0].toLowerCase());
      case 'port':
        return DrilldownDataService.getPortDetails(data.id);
      case 'forecast':
        return DrilldownDataService.getForecastDetails(data.quarter?.toLowerCase().replace(' ', '-'));
      case 'trade':
        return DrilldownDataService.getTradeDetails(data.country?.toLowerCase());
      default:
        return data;
    }
  };

  const detailedData = getDetailedData();

  const renderContent = () => {
    if (!detailedData) return <div>No detailed data available</div>;

    switch(type) {
      // In DrillDownModal.jsx, update the renderContent function to add these cases:

      // In DrillDownModal.jsx, add these cases:

case 'entity':
case 'performance':
case 'share-trend':
  return (
    <div className="drilldown-content">
      <div className="drilldown-header">
        <div className="header-icon" style={{ backgroundColor: data.iconColor || '#4da6ff' }}>
          <i className={data.icon || 'fas fa-ship'}></i>
        </div>
        <div>
          <h4>{data.title}</h4>
          <div className="header-subtitle">
            <span className="insight-category">{data.category || 'Shipping Analysis'}</span>
            <span className="insight-confidence">{data.confidence || 'Medium Confidence'}</span>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="insight-summary">
        <h5><i className="fas fa-file-alt"></i> Executive Summary</h5>
        <div className="summary-content">
          <p className="summary-text">{data.summary}</p>
          {data.details && (
            <p className="details-text">{data.details}</p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {data.keyMetrics && data.keyMetrics.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-chart-bar"></i> Key Performance Metrics</h5>
          <div className="metrics-grid">
            {data.keyMetrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-label">{metric.label}</div>
                <div className="metric-value">{metric.value}</div>
                {metric.change && (
                  <div className={`metric-change ${
                    metric.change.includes('+') || 
                    metric.change.toLowerCase().includes('excellent') ||
                    metric.change.toLowerCase().includes('very good') ||
                    metric.change.toLowerCase().includes('improving') ||
                    metric.change.toLowerCase().includes('increase') ? 'positive' : 
                    metric.change.toLowerCase().includes('needs improvement') ||
                    metric.change.toLowerCase().includes('declining') ? 'negative' : 'neutral'
                  }`}>
                    {metric.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Overview */}
      {data.companyOverview && (
        <div className="details-section">
          <h5><i className="fas fa-building"></i> Company Overview</h5>
          <div className="company-grid">
            {Object.entries(data.companyOverview).map(([key, value], index) => (
              <div key={index} className="company-item">
                <div className="company-label">{key}</div>
                <div className="company-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {data.performanceMetrics && (
        <div className="details-section">
          <h5><i className="fas fa-tachometer-alt"></i> Performance Metrics</h5>
          <div className="performance-grid">
            {typeof data.performanceMetrics === 'object' && !Array.isArray(data.performanceMetrics) ? (
              Object.entries(data.performanceMetrics).map(([key, value], index) => (
                <div key={index} className="performance-item">
                  <div className="performance-label">{key}</div>
                  <div className="performance-value">{value}</div>
                </div>
              ))
            ) : (
              <table className="performance-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Benchmark</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.performanceMetrics.map((metric, index) => (
                    <tr key={index}>
                      <td>{metric.metric || metric.metricName}</td>
                      <td>{metric.value}</td>
                      <td>{metric.benchmark || 'N/A'}</td>
                      <td>
                        <span className={`status-indicator ${
                          metric.status === 'Excellent' ? 'status-high' :
                          metric.status === 'Very Good' ? 'status-high' :
                          metric.status === 'Good' ? 'status-medium' :
                          metric.status === 'Average' ? 'status-medium' : 'status-low'
                        }`}>
                          {metric.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Service Network */}
      {data.serviceNetwork && data.serviceNetwork.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-route"></i> Service Network</h5>
          <ul className="network-list">
            {data.serviceNetwork.map((service, index) => (
              <li key={index}>
                <i className="fas fa-ship"></i> {service}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Vessel Types */}
      {data.vesselTypes && data.vesselTypes.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-anchor"></i> Vessel Fleet Composition</h5>
          <table className="vessel-table">
            <thead>
              <tr>
                <th>Vessel Type</th>
                <th>Count</th>
                <th>Capacity</th>
              </tr>
            </thead>
            <tbody>
              {data.vesselTypes.map((vessel, index) => (
                <tr key={index}>
                  <td>{vessel.type}</td>
                  <td>{vessel.count}</td>
                  <td>{vessel.capacity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Growth Strategy */}
      {data.growthStrategy && data.growthStrategy.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-chart-line"></i> Growth Strategy</h5>
          <ul className="strategy-list">
            {data.growthStrategy.map((strategy, index) => (
              <li key={index}>
                <i className="fas fa-arrow-right"></i> {strategy}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sustainability Initiatives */}
      {data.sustainabilityInitiatives && data.sustainabilityInitiatives.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-leaf"></i> Sustainability Initiatives</h5>
          <ul className="sustainability-list">
            {data.sustainabilityInitiatives.map((initiative, index) => (
              <li key={index}>
                <i className="fas fa-check-circle"></i> {initiative}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent Developments */}
      {data.recentDevelopments && data.recentDevelopments.length > 0 && (
        <div className="details-section info">
          <h5><i className="fas fa-newspaper"></i> Recent Developments</h5>
          <ul className="developments-list">
            {data.recentDevelopments.map((development, index) => (
              <li key={index}>
                <i className="fas fa-bullhorn"></i> {development}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Challenges */}
      {data.challenges && data.challenges.length > 0 && (
        <div className="details-section warning">
          <h5><i className="fas fa-exclamation-triangle"></i> Challenges</h5>
          <ul className="challenges-list">
            {data.challenges.map((challenge, index) => (
              <li key={index}>
                <i className="fas fa-times-circle"></i> {challenge}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Competitive Advantages */}
      {data.competitiveAdvantages && data.competitiveAdvantages.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-trophy"></i> Competitive Advantages</h5>
          <ul className="advantages-list">
            {data.competitiveAdvantages.map((advantage, index) => (
              <li key={index}>
                <i className="fas fa-star"></i> {advantage}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Efficiency Analysis */}
      {data.efficiencyAnalysis && (
        <div className="details-section">
          <h5><i className="fas fa-cogs"></i> Efficiency Analysis</h5>
          <div className="efficiency-grid">
            {Object.entries(data.efficiencyAnalysis).map(([key, value], index) => (
              <div key={index} className="efficiency-item">
                <div className="efficiency-label">{key}</div>
                <div className="efficiency-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Quality */}
      {data.serviceQuality && data.serviceQuality.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-medal"></i> Service Quality Metrics</h5>
          <div className="quality-grid">
            {data.serviceQuality.map((quality, index) => (
              <div key={index} className="quality-item">
                <div className="quality-metric">{quality}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technology Adoption */}
      {data.technologyAdoption && data.technologyAdoption.length > 0 && (
        <div className="details-section info">
          <h5><i className="fas fa-microchip"></i> Technology Adoption</h5>
          <ul className="technology-list">
            {data.technologyAdoption.map((tech, index) => (
              <li key={index}>
                <i className="fas fa-laptop-code"></i> {tech}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Areas for Improvement */}
      {data.areasForImprovement && data.areasForImprovement.length > 0 && (
        <div className="details-section warning">
          <h5><i className="fas fa-tools"></i> Areas for Improvement</h5>
          <ul className="improvement-list">
            {data.areasForImprovement.map((area, index) => (
              <li key={index}>
                <i className="fas fa-wrench"></i> {area}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Comparative Analysis */}
      {data.comparativeAnalysis && data.comparativeAnalysis.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-balance-scale"></i> Comparative Analysis</h5>
          <ul className="comparative-list">
            {data.comparativeAnalysis.map((analysis, index) => (
              <li key={index}>
                <i className="fas fa-chart-bar"></i> {analysis}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic Recommendations */}
      {data.strategicRecommendations && data.strategicRecommendations.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-chess-board"></i> Strategic Recommendations</h5>
          <div className="recommendations-grid">
            {data.strategicRecommendations.map((recommendation, index) => (
              <div key={index} className="recommendation-card">
                <div className="recommendation-number">{index + 1}</div>
                <div className="recommendation-text">{recommendation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Forecast */}
      {data.forecast && (
        <div className="details-section info">
          <h5><i className="fas fa-crystal-ball"></i> Forecast & Outlook</h5>
          <div className="forecast-content">
            <p>{data.forecast}</p>
          </div>
        </div>
      )}
    </div>
  );
case 'trade-partner':
case 'trade-kpi':
case 'commodity-trade':
case 'trade-balance':
case 'trade-growth':
case 'trade-trend':
case 'commodity-trade-comparison':
  return (
    <div className="drilldown-content">
      <div className="drilldown-header">
        <div className="header-icon" style={{ backgroundColor: data.iconColor || '#4da6ff' }}>
          <i className={data.icon || 'fas fa-globe'}></i>
        </div>
        <div>
          <h4>{data.title}</h4>
          <div className="header-subtitle">
            <span className="insight-category">{data.category || 'Trade Analysis'}</span>
            <span className="insight-confidence">{data.confidence || 'Medium Confidence'}</span>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="insight-summary">
        <h5><i className="fas fa-file-alt"></i> Executive Summary</h5>
        <div className="summary-content">
          <p className="summary-text">{data.summary}</p>
          {data.details && (
            <p className="details-text">{data.details}</p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {data.keyMetrics && data.keyMetrics.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-chart-bar"></i> Key Performance Metrics</h5>
          <div className="metrics-grid">
            {data.keyMetrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-label">{metric.label}</div>
                <div className="metric-value">{metric.value}</div>
                {metric.change && (
                  <div className={`metric-change ${
                    metric.change.includes('+') || 
                    metric.change.toLowerCase().includes('growth') ||
                    metric.change.toLowerCase().includes('improving') ||
                    metric.change.toLowerCase().includes('increase') ||
                    metric.change.toLowerCase().includes('narrowing') ? 'positive' : 
                    metric.change.includes('-') ||
                    metric.change.toLowerCase().includes('decline') ||
                    metric.change.toLowerCase().includes('decrease') ||
                    metric.change.toLowerCase().includes('widening') ? 'negative' : 'neutral'
                  }`}>
                    {metric.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trade Metrics */}
      {data.tradeMetrics && data.tradeMetrics.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-exchange-alt"></i> Trade Metrics</h5>
          <div className="metrics-grid">
            {data.tradeMetrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-label">{metric.label}</div>
                <div className="metric-value">{metric.value}</div>
                {metric.change && (
                  <div className={`metric-change ${
                    metric.change.includes('+') ? 'positive' : 
                    metric.change.includes('-') ? 'negative' : 'neutral'
                  }`}>
                    {metric.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import Analysis */}
      {data.importAnalysis && (
        <div className="details-section">
          <h5><i className="fas fa-download"></i> Import Analysis</h5>
          <div className="analysis-grid">
            {Object.entries(data.importAnalysis).map(([key, value], index) => (
              <div key={index} className="analysis-item">
                <div className="analysis-label">{key}</div>
                <div className="analysis-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Analysis */}
      {data.exportAnalysis && (
        <div className="details-section">
          <h5><i className="fas fa-upload"></i> Export Analysis</h5>
          <div className="analysis-grid">
            {Object.entries(data.exportAnalysis).map(([key, value], index) => (
              <div key={index} className="analysis-item">
                <div className="analysis-label">{key}</div>
                <div className="analysis-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trade Balance */}
      {data.tradeBalance && (
        <div className="details-section warning">
          <h5><i className="fas fa-balance-scale"></i> Trade Balance Analysis</h5>
          <div className="balance-grid">
            {Object.entries(data.tradeBalance).map(([key, value], index) => (
              <div key={index} className="balance-item">
                <div className="balance-label">{key}</div>
                <div className={`balance-value ${
                  key.toLowerCase().includes('deficit') || 
                  key.toLowerCase().includes('negative') ? 'negative' : 'positive'
                }`}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Commodity Breakdown */}
      {data.commodityBreakdown && (
        <div className="details-section">
          <h5><i className="fas fa-boxes"></i> Commodity Breakdown</h5>
          <table className="commodity-table">
            <thead>
              <tr>
                <th>Commodity</th>
                <th>Volume</th>
                <th>Share</th>
                <th>Growth</th>
              </tr>
            </thead>
            <tbody>
              {data.commodityBreakdown.map((item, index) => (
                <tr key={index}>
                  <td><strong>{item.commodity}</strong></td>
                  <td>{item.volume}</td>
                  <td>{item.share}</td>
                  <td>
                    <span className={`status-indicator ${
                      item.growth.includes('+') ? 'status-high' : 
                      item.growth.includes('-') ? 'status-low' : 'status-medium'
                    }`}>
                      {item.growth}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Source/Destination Countries */}
      {data.topSourceCountries && (
        <div className="details-section">
          <h5><i className="fas fa-map-marker-alt"></i> Top Source Countries</h5>
          <div className="countries-grid">
            {data.topSourceCountries.map((country, index) => (
              <div key={index} className="country-card">
                <div className="country-name">{country.country}</div>
                <div className="country-share">{country.share}</div>
                <div className="country-volume">{country.volume}</div>
                <div className={`country-trend ${country.trend.toLowerCase()}`}>
                  <i className={`fas fa-arrow-${country.trend === 'Increasing' ? 'up' : 
                    country.trend === 'Decreasing' ? 'down' : 'right'}`}></i>
                  {country.trend}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.topDestinationCountries && (
        <div className="details-section">
          <h5><i className="fas fa-plane-departure"></i> Top Destination Countries</h5>
          <div className="countries-grid">
            {data.topDestinationCountries.map((country, index) => (
              <div key={index} className="country-card">
                <div className="country-name">{country.country}</div>
                <div className="country-share">{country.share}</div>
                <div className="country-volume">{country.volume}</div>
                <div className={`country-trend ${country.trend.toLowerCase().replace(' ', '-')}`}>
                  <i className={`fas fa-arrow-${country.trend.includes('Strong') ? 'up' : 
                    country.trend === 'Stable' ? 'right' : 'up'}`}></i>
                  {country.trend}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import/Export Categories */}
      {data.importCategories && (
        <div className="details-section">
          <h5><i className="fas fa-list-alt"></i> Import Categories</h5>
          <div className="categories-grid">
            {data.importCategories.map((category, index) => (
              <div key={index} className="category-card">
                <div className="category-header">
                  <div className="category-name">{category.category}</div>
                  <div className="category-share">{category.share}</div>
                </div>
                <div className="category-growth">
                  <span>Growth: </span>
                  <span className={category.growth.includes('+') ? 'positive' : 'negative'}>
                    {category.growth}
                  </span>
                </div>
                <div className="category-examples">{category.examples}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.exportCategories && (
        <div className="details-section">
          <h5><i className="fas fa-list-alt"></i> Export Categories</h5>
          <div className="categories-grid">
            {data.exportCategories.map((category, index) => (
              <div key={index} className="category-card">
                <div className="category-header">
                  <div className="category-name">{category.category}</div>
                  <div className="category-share">{category.share}</div>
                </div>
                <div className="category-growth">
                  <span>Growth: </span>
                  <span className={category.growth.includes('+') ? 'positive' : 'negative'}>
                    {category.growth}
                  </span>
                </div>
                <div className="category-examples">{category.examples}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Growth Drivers */}
      {data.growthDrivers && data.growthDrivers.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-bolt"></i> Growth Drivers</h5>
          <ul className="driver-list">
            {data.growthDrivers.map((driver, index) => (
              <li key={index}>
                <i className="fas fa-check-circle"></i> {driver}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Competitive Advantages */}
      {data.competitiveAdvantages && data.competitiveAdvantages.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-trophy"></i> Competitive Advantages</h5>
          <ul className="advantages-list">
            {data.competitiveAdvantages.map((advantage, index) => (
              <li key={index}>
                <i className="fas fa-star"></i> {advantage}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Market Opportunities */}
      {data.marketOpportunities && data.marketOpportunities.length > 0 && (
        <div className="details-section info">
          <h5><i className="fas fa-bullseye"></i> Market Opportunities</h5>
          <ul className="opportunities-list">
            {data.marketOpportunities.map((opportunity, index) => (
              <li key={index}>
                <i className="fas fa-lightbulb"></i> {opportunity}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk Factors */}
      {data.riskFactors && data.riskFactors.length > 0 && (
        <div className="details-section warning">
          <h5><i className="fas fa-exclamation-triangle"></i> Risk Factors</h5>
          <ul className="risks-list">
            {data.riskFactors.map((risk, index) => (
              <li key={index}>
                <i className="fas fa-times-circle"></i> {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic Recommendations */}
      {data.strategicRecommendations && data.strategicRecommendations.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-chess-board"></i> Strategic Recommendations</h5>
          <div className="recommendations-grid">
            {data.strategicRecommendations.map((recommendation, index) => (
              <div key={index} className="recommendation-card">
                <div className="recommendation-number">{index + 1}</div>
                <div className="recommendation-text">{recommendation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Promotion Initiatives */}
      {data.exportPromotionInitiatives && data.exportPromotionInitiatives.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-rocket"></i> Export Promotion Initiatives</h5>
          <ul className="initiatives-list">
            {data.exportPromotionInitiatives.map((initiative, index) => (
              <li key={index}>
                <i className="fas fa-check"></i> {initiative}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Forecast */}
      {data.forecast && (
        <div className="details-section info">
          <h5><i className="fas fa-crystal-ball"></i> Forecast & Outlook</h5>
          <div className="forecast-content">
            <p>{data.forecast}</p>
            {data.outlook && (
              <div className="outlook-details">
                <p><strong>Detailed Outlook:</strong> {data.outlook}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deficit Analysis */}
      {data.deficitAnalysis && (
        <div className="details-section warning">
          <h5><i className="fas fa-chart-line"></i> Deficit Analysis</h5>
          <table className="deficit-table">
            <thead>
              <tr>
                <th>Factor</th>
                <th>Contribution</th>
                <th>Trend</th>
                <th>Recommended Action</th>
              </tr>
            </thead>
            <tbody>
              {data.deficitAnalysis.map((item, index) => (
                <tr key={index}>
                  <td>{item.factor}</td>
                  <td>{item.contribution}</td>
                  <td>
                    <span className={`trend-indicator ${
                      item.trend === 'High' ? 'high' :
                      item.trend === 'Improving' ? 'improving' :
                      item.trend === 'Declining' ? 'declining' : 'stable'
                    }`}>
                      {item.trend}
                    </span>
                  </td>
                  <td>{item.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Trend Data */}
      {data.trendData && (
        <div className="details-section">
          <h5><i className="fas fa-chart-line"></i> Trend Analysis</h5>
          <table className="trend-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Imports</th>
                <th>Exports</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.trendData.map((item, index) => (
                <tr key={index}>
                  <td>{item.month || item.period}</td>
                  <td>{item.imports || item.containerized}</td>
                  <td>{item.exports || item.dryBulk}</td>
                  <td className={item.balance?.includes('-') ? 'negative' : 'positive'}>
                    {item.balance || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Key Observations */}
      {data.keyObservations && data.keyObservations.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-binoculars"></i> Key Observations</h5>
          <ul className="observations-list">
            {data.keyObservations.map((observation, index) => (
              <li key={index}>
                <i className="fas fa-eye"></i> {observation}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic Initiatives */}
      {data.strategicInitiatives && data.strategicInitiatives.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-chess-knight"></i> Strategic Initiatives</h5>
          <div className="initiatives-timeline">
            {data.strategicInitiatives.map((initiative, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">{index + 1}</div>
                <div className="timeline-content">
                  <div className="timeline-text">{initiative}</div>
                  <div className="timeline-period">
                    {index === 0 ? 'Immediate (0-3 months)' : 
                     index === 1 ? 'Short-term (3-6 months)' : 
                     index === 2 ? 'Medium-term (6-12 months)' : 'Long-term (12+ months)'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
case 'insight':
  return (
    <div className="drilldown-content">
      <div className="drilldown-header">
        <div className="header-icon" style={{ backgroundColor: data.iconColor || '#4da6ff' }}>
          <i className={data.icon || 'fas fa-lightbulb'}></i>
        </div>
        <div>
          <h4>{data.title}</h4>
          <div className="header-subtitle">
            <span className="insight-category">{data.category || 'Business Insight'}</span>
            <span className="insight-confidence">{data.confidence || 'High Confidence'}</span>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="insight-summary">
        <h5><i className="fas fa-file-alt"></i> Executive Summary</h5>
        <div className="summary-content">
          <p className="summary-text">{data.summary}</p>
          {data.details && (
            <p className="details-text">{data.details}</p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {data.keyMetrics && data.keyMetrics.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-chart-bar"></i> Key Performance Metrics</h5>
          <div className="metrics-grid">
            {data.keyMetrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-label">{metric.label}</div>
                <div className="metric-value">{metric.value}</div>
                {metric.change && (
                  <div className={`metric-change ${metric.change.includes('+') || metric.change.toLowerCase().includes('improved') ? 'positive' : 
                                      metric.change.includes('-') ? 'negative' : 'neutral'}`}>
                    {metric.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Factors/Reasons */}
      {data.contributingFactors && data.contributingFactors.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-search"></i> Contributing Factors</h5>
          <ul className="factors-list">
            {data.contributingFactors.map((factor, index) => (
              <li key={index}>
                <i className="fas fa-circle"></i> {factor}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.primaryReasons && data.primaryReasons.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-exclamation-circle"></i> Primary Reasons</h5>
          <ul className="reasons-list">
            {data.primaryReasons.map((reason, index) => (
              <li key={index}>
                <i className="fas fa-arrow-right"></i> {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Current Utilization Table */}
      {data.currentUtilization && (
        <div className="details-section">
          <h5><i className="fas fa-chart-pie"></i> Current Utilization</h5>
          <table className="utilization-table">
            <thead>
              <tr>
                <th>Berth/Area</th>
                <th>Occupancy</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.currentUtilization.map((item, index) => (
                <tr key={index}>
                  <td>{item.berth}</td>
                  <td>
                    <div className="occupancy-display">
                      <span>{item.occupancy}</span>
                      <div className="progress-bar small">
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: item.occupancy,
                            backgroundColor: item.status === 'Critical' ? '#ff6384' : 
                                           item.status === 'High' ? '#ff9f40' : 
                                           item.status === 'Normal' ? '#4cd964' : '#4da6ff'
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-indicator ${
                      item.status === 'Critical' ? 'status-low' :
                      item.status === 'High' ? 'status-medium' : 'status-high'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td>
                    {item.status === 'Critical' ? 'Immediate Action' :
                     item.status === 'High' ? 'Monitor Closely' : 'Normal Operations'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Impact Section */}
      {data.impact && (
        <div className="details-section warning">
          <h5><i className="fas fa-bullseye"></i> Business Impact</h5>
          <div className="impact-content">
            <p>{data.impact}</p>
            {data.revenueImpact && (
              <div className="impact-metrics">
                <div className="impact-metric">
                  <span className="label">Revenue Impact:</span>
                  <span className="value">{data.revenueImpact}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Items */}
      {data.immediateActions && data.immediateActions.length > 0 && (
        <div className="details-section danger">
          <h5><i className="fas fa-clock"></i> Immediate Actions Required (Next 30 Days)</h5>
          <div className="action-items">
            {data.immediateActions.map((action, index) => (
              <div key={index} className="action-item urgent">
                <div className="action-number">{index + 1}</div>
                <div className="action-text">{action}</div>
                <div className="action-deadline">30 days</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Steps / Mitigation */}
      {data.nextSteps && data.nextSteps.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-road"></i> Recommended Next Steps</h5>
          <div className="next-steps">
            {data.nextSteps.map((step, index) => (
              <div key={index} className="step-item">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">
                  <div className="step-text">{step}</div>
                  {data.timeline && index === 0 && (
                    <div className="step-timeline">
                      <i className="fas fa-calendar"></i> Timeline: {data.timeline}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.mitigationStrategies && data.mitigationStrategies.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-shield-alt"></i> Mitigation Strategies</h5>
          <div className="mitigation-grid">
            {data.mitigationStrategies.map((strategy, index) => (
              <div key={index} className="strategy-card">
                <div className="strategy-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="strategy-text">{strategy}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Best Practices */}
      {data.bestPractices && data.bestPractices.length > 0 && (
        <div className="details-section info">
          <h5><i className="fas fa-star"></i> Best Practices Implemented</h5>
          <div className="practices-grid">
            {data.bestPractices.map((practice, index) => (
              <div key={index} className="practice-item">
                <div className="practice-icon">
                  <i className="fas fa-award"></i>
                </div>
                <div className="practice-text">{practice}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sustainability Benefits */}
      {data.sustainabilityBenefits && data.sustainabilityBenefits.length > 0 && (
        <div className="details-section success">
          <h5><i className="fas fa-leaf"></i> Sustainability Benefits</h5>
          <ul className="sustainability-list">
            {data.sustainabilityBenefits.map((benefit, index) => (
              <li key={index}>
                <i className="fas fa-check"></i> {benefit}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risks */}
      {data.risks && data.risks.length > 0 && (
        <div className="details-section warning">
          <h5><i className="fas fa-exclamation-triangle"></i> Identified Risks</h5>
          <ul className="risks-list">
            {data.risks.map((risk, index) => (
              <li key={index}>
                <i className="fas fa-times-circle"></i> {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Long Term Solutions */}
      {data.longTermSolutions && data.longTermSolutions.length > 0 && (
        <div className="details-section">
          <h5><i className="fas fa-calendar-plus"></i> Long-term Solutions (6-24 Months)</h5>
          <div className="solutions-timeline">
            {data.longTermSolutions.map((solution, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">{index + 1}</div>
                <div className="timeline-content">
                  <div className="timeline-text">{solution}</div>
                  <div className="timeline-period">
                    {index === 0 ? '6-12 months' : 
                     index === 1 ? '12-18 months' : 
                     index === 2 ? '18-24 months' : '24+ months'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
      case 'overall-kpi':
  return (
    <div className="drilldown-content">
      <div className="drilldown-header">
        <div className="header-icon" style={{ backgroundColor: data.color || '#4da6ff' }}>
          <i className={data.icon || 'fas fa-chart-line'}></i>
        </div>
        <div>
          <h4>{data.title}</h4>
          <div className="header-subtitle">{data.description}</div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Current Value</div>
          <div className="stat-value">{data.value}</div>
          {data.growth && (
            <div className="stat-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>{data.growth}</span>
            </div>
          )}
          {data.change && (
            <div className="stat-change positive">
              <i className="fas fa-arrow-up"></i>
              <span>{data.change}</span>
            </div>
          )}
        </div>

        {data.comparison && (
          <div className="stat-card">
            <div className="stat-label">Industry Comparison</div>
            <div className="stat-value">{data.comparison.split(':')[1].trim()}</div>
            <div className="stat-change">
              <i className="fas fa-chart-bar"></i>
              <span>Benchmark</span>
            </div>
          </div>
        )}

        {data.avgSize && (
          <div className="stat-card">
            <div className="stat-label">Average Size</div>
            <div className="stat-value">{data.avgSize}</div>
            <div className="stat-change">
              <i className="fas fa-ruler"></i>
              <span>Vessel capacity</span>
            </div>
          </div>
        )}

        {data.efficiency && (
          <div className="stat-card">
            <div className="stat-label">Efficiency</div>
            <div className="stat-value">{data.efficiency.includes('faster') ? 'Excellent' : 'Good'}</div>
            <div className="stat-change positive">
              <i className="fas fa-bolt"></i>
              <span>{data.efficiency}</span>
            </div>
          </div>
        )}
      </div>

      {/* Breakdown Section */}
      {data.breakdown && (
        <div className="details-section">
          <h5><i className="fas fa-pie-chart"></i> Breakdown Analysis</h5>
          <div className="breakdown-grid">
            {data.breakdown.map((item, index) => (
              <div key={index} className="breakdown-item">
                <div className="breakdown-category">{item.category}</div>
                <div className="breakdown-value">{item.value}</div>
                <div className="breakdown-percentage">{item.percentage}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vessel Types Section */}
      {data.vesselTypes && (
        <div className="details-section">
          <h5><i className="fas fa-ship"></i> Vessel Type Distribution</h5>
          <div className="vessel-types-grid">
            {data.vesselTypes.map((vessel, index) => (
              <div key={index} className="vessel-type-item">
                <div className="vessel-type-name">{vessel.type}</div>
                <div className="vessel-type-count">{vessel.count} vessels</div>
                <div className="vessel-type-percentage">{vessel.percentage}</div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: vessel.percentage,
                      backgroundColor: data.color
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Berth Details Section */}
      {data.berthDetails && (
        <div className="details-section">
          <h5><i className="fas fa-dock"></i> Berth Utilization Details</h5>
          <table className="data-table">
            <thead>
              <tr>
                <th>Berth Number</th>
                <th>Type</th>
                <th>Occupancy</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.berthDetails.map((berth, index) => (
                <tr key={index}>
                  <td>{berth.number}</td>
                  <td>{berth.type}</td>
                  <td>
                    <div className="occupancy-display">
                      <span>{berth.occupancy}</span>
                      <div className="progress-bar small">
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: berth.occupancy,
                            backgroundColor: berth.status === 'Critical' ? '#ff6384' : 
                                           berth.status === 'Normal' ? '#4cd964' : '#ff9f40'
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-indicator ${
                      berth.status === 'Critical' ? 'status-low' :
                      berth.status === 'Normal' ? 'status-high' : 'status-medium'
                    }`}>
                      {berth.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Key Drivers Section */}
      {data.keyDrivers && (
        <div className="details-section success">
          <h5><i className="fas fa-bolt"></i> Key Performance Drivers</h5>
          <ul className="driver-list">
            {data.keyDrivers.map((driver, index) => (
              <li key={index}>
                <i className="fas fa-check-circle"></i> {driver}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations Section */}
      {data.recommendations && (
        <div className="details-section warning">
          <h5><i className="fas fa-lightbulb"></i> Recommendations</h5>
          <ul className="recommendation-list">
            {data.recommendations.map((rec, index) => (
              <li key={index}>
                <i className="fas fa-arrow-right"></i> {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Items Section */}
      {data.actionItems && (
        <div className="details-section">
          <h5><i className="fas fa-tasks"></i> Action Items</h5>
          <div className="action-items-grid">
            {data.actionItems.map((action, index) => (
              <div key={index} className="action-item">
                <div className="action-number">{index + 1}</div>
                <div className="action-text">{action}</div>
                <div className="action-status">
                  <span className="status-indicator status-medium">Pending</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
      case 'commodity':
      case 'commodity-details':
        return (
          <div className="drilldown-content">
            <div className="drilldown-header">
              <div className="header-icon" style={{ backgroundColor: detailedData.color || '#4da6ff' }}>
                <i className={detailedData.icon || 'fas fa-box'}></i>
              </div>
              <div>
                <h4>{detailedData.name}</h4>
                <div className="header-subtitle">{detailedData.category} • {detailedData.description}</div>
              </div>
            </div>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Current Month</div>
                <div className="stat-value">{detailedData.current}</div>
                <div className="stat-change">
                  <i className={`fas fa-arrow-${detailedData.growth.includes('+') ? 'up' : 'down'}`}></i>
                  {detailedData.growth} from last month
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Market Share</div>
                <div className="stat-value">{detailedData.share}</div>
                <div className="stat-change">of total throughput</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Value per Ton</div>
                <div className="stat-value">{detailedData.valuePerTon || '$530'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Storage Days</div>
                <div className="stat-value">{detailedData.storageDays || '4.5 days'}</div>
              </div>
            </div>

            <div className="details-sections">
              {/* Routes and Products */}
              <div className="details-section">
                <h5><i className="fas fa-route"></i> Top Routes & Products</h5>
                <div className="section-content">
                  <p><strong>Main Routes:</strong> {detailedData.topRoutes}</p>
                  <p><strong>Key Products:</strong> {detailedData.mainProducts}</p>
                  <p><strong>Vessel Types:</strong> {detailedData.vesselTypes?.join(', ')}</p>
                  <p><strong>Handling Time:</strong> {detailedData.handlingTime}</p>
                </div>
              </div>

              {/* Country Breakdown */}
              <div className="details-section">
                <h5><i className="fas fa-globe-americas"></i> Top Countries</h5>
                <div className="country-grid">
                  {detailedData.topCountries?.map((country, index) => (
                    <div key={index} className="country-item">
                      <div className="country-name">{country.country}</div>
                      <div className="country-volume">{country.volume}</div>
                      <div className="country-share">{country.share}</div>
                      <div className={`country-trend ${country.trend}`}>
                        <i className={`fas fa-arrow-${country.trend === 'increasing' ? 'up' : country.trend === 'decreasing' ? 'down' : 'right'}`}></i>
                        {country.trend}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quarterly Performance */}
              <div className="details-section">
                <h5><i className="fas fa-chart-line"></i> Quarterly Performance (2023)</h5>
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Quarter</th>
                      <th>Volume</th>
                      <th>Growth</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailedData.quarterlyPerformance?.map((q, index) => (
                      <tr key={index}>
                        <td>{q.quarter}</td>
                        <td>{q.volume}</td>
                        <td className={q.growth.includes('+') ? 'positive' : 'negative'}>{q.growth}</td>
                        <td>{q.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Monthly Trend */}
              <div className="details-section">
                <h5><i className="fas fa-calendar-alt"></i> Monthly Trend (Current Year)</h5>
                <div className="trend-chart">
                  {detailedData.monthlyTrend?.map((month, index) => (
                    <div key={index} className="trend-item">
                      <div className="trend-month">{month.month}</div>
                      <div className="trend-bar">
                        <div 
                          className="trend-fill" 
                          style={{ 
                            height: `${Math.min(100, (parseInt(month.volume) / 100000) * 100)}%`,
                            backgroundColor: detailedData.color
                          }}
                        ></div>
                      </div>
                      <div className="trend-volume">{month.volume}</div>
                      <div className={`trend-change ${month.change.includes('+') ? 'positive' : 'negative'}`}>
                        {month.change}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk & Opportunities */}
              <div className="dual-column">
                <div className="details-section warning">
                  <h5><i className="fas fa-exclamation-triangle"></i> Risk Factors</h5>
                  <ul>
                    {detailedData.riskFactors?.map((risk, index) => (
                      <li key={index}>{risk}</li>
                    ))}
                  </ul>
                </div>
                <div className="details-section success">
                  <h5><i className="fas fa-lightbulb"></i> Opportunities</h5>
                  <ul>
                    {detailedData.opportunities?.map((opportunity, index) => (
                      <li key={index}>{opportunity}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'entity':
      case 'entity-traffic':
        return (
          <div className="drilldown-content">
            <div className="drilldown-header">
              <div className="entity-logo-large" style={{ backgroundColor: detailedData.color || '#ff6384' }}>
                {detailedData.shortName || 'E'}
              </div>
              <div>
                <h4>{detailedData.name}</h4>
                <div className="header-subtitle">
                  {detailedData.parentCompany} • Founded {detailedData.founded} • {detailedData.headquarters}
                </div>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Market Share</div>
                <div className="stat-value">{detailedData.marketShare}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Monthly Volume</div>
                <div className="stat-value">{detailedData.volume}</div>
                <div className="stat-change positive">
                  <i className="fas fa-arrow-up"></i>
                  {detailedData.growth} from last month
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Vessels Deployed</div>
                <div className="stat-value">{detailedData.vessels}</div>
                <div className="stat-change">Fleet: {detailedData.fleetSize}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">On-time Performance</div>
                <div className="stat-value">{detailedData.onTimePerformance}</div>
                <div className="stat-change">Rating: {detailedData.customerRating}</div>
              </div>
            </div>

            <div className="details-sections">
              {/* Key Information */}
              <div className="dual-column">
                <div className="details-section">
                  <h5><i className="fas fa-info-circle"></i> Key Information</h5>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Total Capacity:</span>
                      <span className="info-value">{detailedData.totalCapacity}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Avg Turnaround:</span>
                      <span className="info-value">{detailedData.avgTurnaround}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Vessel Types:</span>
                      <span className="info-value">{detailedData.vesselTypes?.join(', ')}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Services:</span>
                      <span className="info-value">{detailedData.services?.join(', ')}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h5><i className="fas fa-route"></i> Key Routes</h5>
                  <ul className="route-list">
                    {detailedData.keyRoutes?.map((route, index) => (
                      <li key={index}>{route}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Quarterly Performance */}
              <div className="details-section">
                <h5><i className="fas fa-chart-bar"></i> Quarterly Performance (2023)</h5>
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Quarter</th>
                      <th>Volume</th>
                      <th>Growth</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailedData.quarterlyPerformance?.map((q, index) => (
                      <tr key={index}>
                        <td>{q.quarter}</td>
                        <td>{q.volume}</td>
                        <td className={q.growth.includes('+') ? 'positive' : 'negative'}>{q.growth}</td>
                        <td>{q.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Financial & Sustainability */}
              <div className="dual-column">
                <div className="details-section">
                  <h5><i className="fas fa-chart-line"></i> Financial Metrics</h5>
                  <div className="info-grid">
                    {detailedData.financialMetrics && Object.entries(detailedData.financialMetrics).map(([key, value], index) => (
                      <div key={index} className="info-item">
                        <span className="info-label">{key.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="info-value">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="details-section">
                  <h5><i className="fas fa-leaf"></i> Sustainability</h5>
                  <div className="info-grid">
                    {detailedData.sustainability && Object.entries(detailedData.sustainability).map(([key, value], index) => (
                      <div key={index} className="info-item">
                        <span className="info-label">{key.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="info-value">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Performance */}
              {detailedData.monthlyPerformance && (
                <div className="details-section">
                  <h5><i className="fas fa-calendar-check"></i> Recent Performance</h5>
                  <div className="monthly-performance">
                    {detailedData.monthlyPerformance.map((month, index) => (
                      <div key={index} className="month-item">
                        <div className="month-name">{month.month}</div>
                        <div className="month-stats">
                          <span className="stat">Vessel Calls: {month.calls}</span>
                          <span className="stat">Volume: {month.volume}</span>
                          <span className="stat">On-time: {month.onTime}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'port':
        return (
          <div className="drilldown-content">
            <div className="drilldown-header">
              <div className="header-icon" style={{ backgroundColor: detailedData.color || '#4da6ff' }}>
                <i className="fas fa-ship"></i>
              </div>
              <div>
                <h4>{detailedData.name}</h4>
                <div className="header-subtitle">
                  {detailedData.location} • Established {detailedData.established} • Operated by {detailedData.operator}
                </div>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Monthly Volume</div>
                <div className="stat-value">{detailedData.volume}</div>
                <div className="stat-change positive">
                  <i className="fas fa-arrow-up"></i>
                  {detailedData.growth} from last month
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Vessels Handled</div>
                <div className="stat-value">{detailedData.vessels}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Berth Occupancy</div>
                <div className="stat-value">{detailedData.occupancy}</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: detailedData.occupancy }}></div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Avg Turnaround</div>
                <div className="stat-value">{detailedData.turnaround}</div>
              </div>
            </div>

            <div className="details-sections">
              {/* Infrastructure */}
              <div className="details-section">
                <h5><i className="fas fa-industry"></i> Infrastructure</h5>
                <div className="infrastructure-grid">
                  <div className="infra-item">
                    <i className="fas fa-dock"></i>
                    <div className="infra-value">{detailedData.berths || '22'}</div>
                    <div className="infra-label">Berths</div>
                  </div>
                  <div className="infra-item">
                    <i className="fas fa-crane"></i>
                    <div className="infra-value">{detailedData.cranes || '45'}</div>
                    <div className="infra-label">Cranes</div>
                  </div>
                  <div className="infra-item">
                    <i className="fas fa-warehouse"></i>
                    <div className="infra-value">{detailedData.storageCapacity || '1.2M TEU'}</div>
                    <div className="infra-label">Storage Capacity</div>
                  </div>
                  <div className="infra-item">
                    <i className="fas fa-ruler-combined"></i>
                    <div className="infra-value">{detailedData.area || '830 ha'}</div>
                    <div className="infra-label">Area</div>
                  </div>
                </div>
              </div>

              {/* Commodity Breakdown */}
              <div className="details-section">
                <h5><i className="fas fa-boxes"></i> Commodity Breakdown</h5>
                <div className="commodity-breakdown">
                  {detailedData.keyCommodities?.map((commodity, index) => (
                    <div key={index} className="commodity-item">
                      <div className="commodity-name">{commodity.name}</div>
                      <div className="commodity-share">{commodity.share}</div>
                      <div className="commodity-volume">{commodity.volume}</div>
                      <div className="commodity-bar">
                        <div 
                          className="commodity-fill" 
                          style={{ 
                            width: commodity.share,
                            backgroundColor: index === 0 ? '#4da6ff' : 
                                           index === 1 ? '#ff9f40' : 
                                           index === 2 ? '#ff6384' : '#4cd964'
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services & Connectivity */}
              <div className="dual-column">
                <div className="details-section">
                  <h5><i className="fas fa-concierge-bell"></i> Services Offered</h5>
                  <ul className="service-list">
                    {detailedData.services?.map((service, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i> {service}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="details-section">
                  <h5><i className="fas fa-network-wired"></i> Connectivity</h5>
                  <div className="connectivity-grid">
                    {detailedData.connectivity && Object.entries(detailedData.connectivity).map(([key, value], index) => (
                      <div key={index} className="connectivity-item">
                        <div className="connectivity-type">{key}</div>
                        <div className="connectivity-value">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Expansion Projects */}
              {detailedData.expansionProjects && (
                <div className="details-section">
                  <h5><i className="fas fa-hard-hat"></i> Expansion Projects</h5>
                  <div className="projects-grid">
                    {detailedData.expansionProjects.map((project, index) => (
                      <div key={index} className="project-card">
                        <div className="project-name">{project.name}</div>
                        <div className="project-details">
                          <span className="detail">Completion: {project.completion}</span>
                          <span className="detail">Capacity: {project.capacity || project.area}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Performance Metrics */}
              {detailedData.performanceMetrics && (
                <div className="details-section">
                  <h5><i className="fas fa-tachometer-alt"></i> Performance Metrics</h5>
                  <div className="metrics-grid">
                    {Object.entries(detailedData.performanceMetrics).map(([key, value], index) => (
                      <div key={index} className="metric-item">
                        <div className="metric-name">{key.replace(/([A-Z])/g, ' $1')}</div>
                        <div className="metric-value">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      // Add other types (forecast, trade, etc.) similarly...

      default:
        return (
          <div className="drilldown-content">
            <div className="drilldown-header">
              <div className="header-icon" style={{ backgroundColor: '#4da6ff' }}>
                <i className="fas fa-info-circle"></i>
              </div>
              <div>
                <h4>{title || 'Detailed View'}</h4>
              </div>
            </div>
            <div className="default-content">
              <p>Detailed information for {detailedData.name || 'this item'}</p>
              {detailedData && Object.entries(detailedData).map(([key, value]) => (
                <div key={key} className="data-row">
                  <span className="data-key">{key.replace(/([A-Z])/g, ' $1')}:</span>
                  <span className="data-value">
                    {typeof value === 'object' ? JSON.stringify(value) : value?.toString() || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="drilldown-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title || detailedData?.name || 'Detailed View'}</h3>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {renderContent()}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-times"></i> Close
          </button>
          <button className="btn btn-primary" onClick={() => {
            alert(`Exporting ${detailedData?.name || 'data'} as PDF...`);
            // Add actual export functionality here
          }}>
            <i className="fas fa-download"></i> Export as PDF
          </button>
          <button className="btn btn-info" onClick={() => {
            // Add share functionality
            alert('Sharing feature coming soon...');
          }}>
            <i className="fas fa-share-alt"></i> Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;